import { useState } from 'react'
import { useForm, router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { CircleCheck } from 'lucide-react'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import SettingsLayout from '@/layouts/SettingsLayout'
import { useOrgPath } from '@/lib/pathFor'
import { cn } from '@/lib/utils'

interface Organization {
  name: string
  slug: string
  internal_domain: string | null
  imap_host: string | null
  imap_port: number | null
  imap_user: string | null
  imap_password: string | null
  imap_mailbox: string | null
  imap_auth_type: 'password' | 'xoauth2'
  imap_oauth_provider: string | null
  imap_oauth_connected: boolean
  imap_oauth_email: string | null
  google_oauth_available: boolean
  outbound_email_provider: 'smtp' | 'postmark'
  outbound_from_name: string | null
  smtp_from_address: string | null
  outbound_reply_to_address: string | null
  effective_from_address: string | null
  effective_reply_to_address: string | null
  imap_configured: boolean
  smtp_configured: boolean
}

interface Props {
  organization: Organization
}

export default function SettingsGeneral({ organization }: Props) {
  const pathFor = useOrgPath()

  const { data, setData, patch, processing } = useForm({
    internal_domain: organization.internal_domain || '',
    imap_host: organization.imap_host || '',
    imap_port: organization.imap_port?.toString() || '993',
    imap_user: organization.imap_user || '',
    imap_password: '',
    imap_mailbox: organization.imap_mailbox || 'INBOX',
    outbound_email_provider: organization.outbound_email_provider || 'smtp',
    outbound_from_name: organization.outbound_from_name || 'XPORT Operations',
    smtp_from_address: organization.smtp_from_address || '',
    outbound_reply_to_address: organization.outbound_reply_to_address || '',
  })

  // Which inbound method the IMAP section is showing. Only relevant when no
  // Google inbox is connected yet; the connected state is driven by the server.
  const [imapProvider, setImapProvider] = useState<'password' | 'google'>(
    organization.imap_auth_type === 'xoauth2' ? 'google' : 'password',
  )

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(pathFor('/settings'))
  }

  function handleDisconnectGoogle() {
    if (!window.confirm('Disconnect the Google inbox? Email syncing will stop until reconnected.')) return
    router.delete(pathFor('/settings/google_imap'), { preserveScroll: true })
  }

  return (
    <>
      <header className="flex h-14 items-center justify-between border-b px-8">
        <h1 className="text-sm font-semibold">Settings</h1>
        <Button
          type="submit"
          form="settings-form"
          size="sm"
          className="text-xs"
          disabled={processing}
        >
          Save Changes
        </Button>
      </header>

      <div className="max-w-2xl overflow-y-auto p-8">
        <form id="settings-form" onSubmit={handleSubmit} className="space-y-8">
          {/* General */}
          <section>
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              General
            </h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="internal_domain" className="text-xs">Internal Domain</Label>
                <Input
                  id="internal_domain"
                  placeholder="yourcompany.com"
                  value={data.internal_domain}
                  onChange={(e) => setData('internal_domain', e.target.value)}
                  className="mt-1 h-9 text-[13px]"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Emails from this domain are treated as internal (sent by staff).
                </p>
              </div>
            </div>
          </section>

          {/* IMAP Settings */}
          <section>
            <div className="mb-4 flex items-center gap-2">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Inbound Email (IMAP)
              </h2>
              {organization.imap_configured && (
                <Badge
                  variant="secondary"
                  size="sm"
                  className="bg-emerald-50 text-emerald-600"
                >
                  <CircleCheck className="mr-1 h-3 w-3" />
                  Configured
                </Badge>
              )}
            </div>

            {organization.imap_oauth_connected ? (
              /* A Google inbox is connected — the connection is managed by OAuth. */
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="flex items-center gap-3">
                    <CircleCheck className="h-5 w-5 text-emerald-600" />
                    <div>
                      <p className="text-[13px] font-medium">Connected with Google</p>
                      <p className="text-xs text-muted-foreground">
                        {organization.imap_oauth_email || organization.imap_user}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={handleDisconnectGoogle}
                  >
                    Disconnect
                  </Button>
                </div>
                <div>
                  <Label htmlFor="imap_mailbox" className="text-xs">Mailbox</Label>
                  <Input
                    id="imap_mailbox"
                    placeholder="INBOX"
                    value={data.imap_mailbox}
                    onChange={(e) => setData('imap_mailbox', e.target.value)}
                    className="mt-1 h-9 text-[13px]"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Host, port and user are managed automatically for Google.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Auth-method toggle: generic IMAP password vs Google OAuth */}
                <div className="inline-flex rounded-lg border p-0.5">
                  {(['password', 'google'] as const).map((provider) => (
                    <button
                      key={provider}
                      type="button"
                      onClick={() => setImapProvider(provider)}
                      className={cn(
                        'cursor-pointer rounded-md px-3 py-1 text-xs font-medium transition-colors',
                        imapProvider === provider
                          ? 'bg-muted text-foreground'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {provider === 'password' ? 'IMAP / App Password' : 'Google'}
                    </button>
                  ))}
                </div>

                {imapProvider === 'google' ? (
                  <div className="rounded-lg border p-4">
                    {organization.google_oauth_available ? (
                      <>
                        <p className="text-[13px]">Connect a Google account to sync its inbox over IMAP.</p>
                        <p className="mb-3 text-xs text-muted-foreground">
                          You&apos;ll be redirected to Google to grant access. Host, port and user are
                          configured automatically.
                        </p>
                        <Button asChild size="sm" className="text-xs">
                          <a href={pathFor('/settings/google_imap/connect')}>Connect Google account</a>
                        </Button>
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Google OAuth isn&apos;t configured on this server. Set GOOGLE_OAUTH_CLIENT_ID and
                        GOOGLE_OAUTH_CLIENT_SECRET to enable it.
                      </p>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="imap_host" className="text-xs">Host</Label>
                        <Input
                          id="imap_host"
                          placeholder="imap.gmail.com"
                          value={data.imap_host}
                          onChange={(e) => setData('imap_host', e.target.value)}
                          className="mt-1 h-9 text-[13px]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="imap_port" className="text-xs">Port</Label>
                        <Input
                          id="imap_port"
                          type="number"
                          placeholder="993"
                          value={data.imap_port}
                          onChange={(e) => setData('imap_port', e.target.value)}
                          className="mt-1 h-9 text-[13px]"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="imap_user" className="text-xs">User</Label>
                        <Input
                          id="imap_user"
                          placeholder="ops@yourcompany.com"
                          value={data.imap_user}
                          onChange={(e) => setData('imap_user', e.target.value)}
                          className="mt-1 h-9 text-[13px]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="imap_password" className="text-xs">Password</Label>
                        <Input
                          id="imap_password"
                          type="password"
                          placeholder={organization.imap_password ? 'Leave blank to keep current' : 'App password'}
                          value={data.imap_password}
                          onChange={(e) => setData('imap_password', e.target.value)}
                          className="mt-1 h-9 text-[13px]"
                        />
                        {organization.imap_password && (
                          <p className="mt-1 text-xs text-muted-foreground">Leave blank to keep existing password.</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="imap_mailbox" className="text-xs">Mailbox</Label>
                      <Input
                        id="imap_mailbox"
                        placeholder="INBOX"
                        value={data.imap_mailbox}
                        onChange={(e) => setData('imap_mailbox', e.target.value)}
                        className="mt-1 h-9 text-[13px]"
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </section>

          {/* Outbound Email */}
          <section>
            <div className="mb-4 flex items-center gap-2">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Outbound Email
              </h2>
              {(organization.outbound_email_provider === 'postmark' || organization.smtp_configured) && (
                <Badge
                  variant="secondary"
                  size="sm"
                  className="bg-emerald-50 text-emerald-600"
                >
                  <CircleCheck className="mr-1 h-3 w-3" />
                  Configured
                </Badge>
              )}
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="outbound_from_name" className="text-xs">From Name</Label>
                  <Input
                    id="outbound_from_name"
                    type="text"
                    placeholder="XPORT Operations"
                    value={data.outbound_from_name}
                    onChange={(e) => setData('outbound_from_name', e.target.value)}
                    className="mt-1 h-9 text-[13px]"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Displayed next to the sender address.
                  </p>
                </div>
                <div>
                  <Label htmlFor="smtp_from_address" className="text-xs">From Address</Label>
                  <Input
                    id="smtp_from_address"
                    type="text"
                    placeholder="ops@yourcompany.com"
                    value={data.smtp_from_address}
                    onChange={(e) => setData('smtp_from_address', e.target.value)}
                    className="mt-1 h-9 text-[13px]"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Must be verified with the outbound provider.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="outbound_reply_to_address" className="text-xs">Reply-To Address</Label>
                  <Input
                    id="outbound_reply_to_address"
                    type="text"
                    placeholder={organization.effective_reply_to_address || 'Use IMAP user address'}
                    value={data.outbound_reply_to_address}
                    onChange={(e) => setData('outbound_reply_to_address', e.target.value)}
                    className="mt-1 h-9 text-[13px]"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Leave blank to use the IMAP user address.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </form>
      </div>
    </>
  )
}

SettingsGeneral.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout>
    <SettingsLayout>{page}</SettingsLayout>
  </AuthenticatedLayout>
)
